import { UserManager, WebStorageStateStore, type User as OidcUser } from 'oidc-client-ts';
import {
  AuthSessionSchema,
  UserSchema,
  type AuthSession,
  type User,
} from '@voll-intelligence/domain';
import { env } from '@web/core';
import { trimTrailingSlashes } from '../url';

export interface CognitoTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string | null;
  expiresAt: number;
  tokenType: string;
}

interface CognitoExchangeResult {
  tokens: CognitoTokens;
  user: User;
  session: AuthSession;
  returnTo: string | null;
}

export interface CognitoService {
  beginSsoLogin: (opts?: { returnTo?: string }) => Promise<void>;
  exchangeCodeForTokens: () => Promise<CognitoExchangeResult>;
  refreshTokens: (prev: CognitoTokens) => Promise<CognitoTokens>;
  signOut: () => Promise<void>;
}

interface AppState {
  returnTo: string | null;
}

function base64UrlDecode(input: string): string {
  const padded = input.replaceAll('-', '+').replaceAll('_', '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  return atob(padded + pad);
}

export function createCognitoService(): CognitoService {
  const authority = env.VITE_COGNITO_AUTHORITY;
  const clientId = env.VITE_COGNITO_CLIENT_ID;
  if (!authority || !clientId) throw new Error('Missing Cognito Environment Variables');

  const manager = new UserManager({
    authority: trimTrailingSlashes(authority),
    client_id: clientId,
    redirect_uri: `${globalThis.location.origin}/auth/callback`,
    response_type: 'code',
    // Hardcoded — these scopes are an app contract, not deployment config:
    //   - `openid`  : required for OIDC
    //   - `email`   : exposes `email` claim (used by accounts-api JIT)
    //   - `profile` : exposes `name`/`given_name`/`family_name` claims
    //                 (used by accounts-api JIT and the UI user menu)
    // Making this configurable led to silent breakage when a deploy
    // environment override dropped `profile` — name was never populated.
    scope: 'openid email profile',
    loadUserInfo: false,
    monitorSession: false,
    automaticSilentRenew: false,
    userStore: new WebStorageStateStore({
      store: globalThis.sessionStorage, // was globalThis.localStorage — no refresh token in localStorage
      prefix: 'voll.auth.user.',
    }),
    stateStore: new WebStorageStateStore({
      store: globalThis.sessionStorage,
      prefix: 'voll.auth.state.',
    }),
  });

  function userToTokens(user: OidcUser): CognitoTokens {
    if (!user.id_token) throw new Error('Cognito response missing id_token.');
    return {
      accessToken: user.access_token,
      idToken: user.id_token,
      refreshToken: user.refresh_token ?? null,
      expiresAt: (user.expires_at ?? 0) * 1000,
      tokenType: user.token_type,
    };
  }

  function decodeIdToken(idToken: string): { user: User; session: AuthSession } {
    const parts = idToken.split('.');
    const payload = parts[1];
    if (parts.length !== 3 || !payload) throw new Error('Malformed ID token');
    const claims = JSON.parse(base64UrlDecode(payload)) as Record<string, unknown>;

    const user = UserSchema.parse({
      id: claims.sub,
      name: claims.name ?? claims['cognito:username'] ?? claims.email,
      email: claims.email,
    });

    const session = AuthSessionSchema.parse({
      userId: claims.sub,
      tokenUse: 'id',
      groups: claims['cognito:groups'] ?? [],
    });

    return { user, session };
  }

  async function beginSsoLogin(opts?: { returnTo?: string }): Promise<void> {
    const state: AppState = { returnTo: opts?.returnTo ?? null };
    const extraQueryParams = env.VITE_COGNITO_IDENTITY_PROVIDER
      ? { identity_provider: env.VITE_COGNITO_IDENTITY_PROVIDER }
      : undefined;
    await manager.signinRedirect({ state, extraQueryParams });
  }

  async function exchangeCodeForTokens(): Promise<CognitoExchangeResult> {
    const u = await manager.signinRedirectCallback();
    const appState = (u.state as AppState | undefined) ?? null;
    const { user, session } = decodeIdToken(u.id_token as string);
    return {
      tokens: userToTokens(u),
      user,
      session,
      returnTo: appState?.returnTo ?? null,
    };
  }

  async function refreshTokens(prev: CognitoTokens): Promise<CognitoTokens> {
    if (!prev.refreshToken) throw new Error('No refresh token available.');
    const u = await manager.signinSilent();
    if (!u) throw new Error('Silent refresh returned no user.');
    return userToTokens(u);
  }

  async function signOut(): Promise<void> {
    await manager.removeUser();
  }

  return { beginSsoLogin, exchangeCodeForTokens, refreshTokens, signOut };
}
